import React from 'react'
import { i18n } from '@fs/zion-locale'
import { baseUrl } from '@fs/zion-config'
import StructuredData from './StructuredData'
import { getCourseData } from './CourseStructuredData'
import { getCreativeWorkSeriesData } from './CreativeWorkSeriesStructuredData'
import { getEventSeriesData, getRootsTechEventSeriesData } from './EventSeriesStructuredData'
import { getOrganizationData, getRootsTechOrganizationData } from './OrganizationStructuredData'
import { getWebSiteData } from './WebSiteStructuredData'
import { getPersonData } from './PersonStructuredData'
import { getQuestionData } from './QuestionStructuredData'
import {
  getCallForContentEventData,
  getCallForExhibitorsEventData,
  getEventData,
  getRegistrationEventData,
} from './EventStructuredData'
import { getPlaceData } from './PlaceStructuredData'
import { getPromotionOfferData } from './OfferStructuredData'
import { getItemListData } from './ItemListStructuredData'
import { stripHtml } from '../../api/helpers'

const parseFaqQuestionAndAnswers = (nodes) => {
  const result = []
  for (let i = 0; i < nodes.childNodes.length; i++) {
    const node = nodes.childNodes[i]
    // If the node is an h3, its innerText is taken as the question
    if (node.tagName === 'H3') {
      const question = node.innerText
      let answer = ''

      // Iterate through the following nodes to build the answer
      for (i++; i < nodes.childNodes.length; i++) {
        const currentNode = nodes.childNodes[i]

        // If the node is text, it is added to the answer
        if (currentNode.nodeName === '#text') {
          answer += currentNode.nodeValue
        }
        // If the node is a list (ul or ol), extract its text content
        else if (currentNode.tagName === 'UL' || currentNode.tagName === 'OL') {
          const listItems = Array.from(currentNode.querySelectorAll('li'))
          const listText = listItems.map((li) => `- ${li.innerText.trim()}`).join('\n')
          answer += `\n${listText}`
        }
        // If another header is found, processing stops
        else if (currentNode.tagName.startsWith('H')) {
          break
        }
      }

      // Clean the answer by removing unnecessary spaces
      answer = answer.replace(/\s+/g, ' ').trim()
      result.push({ question, answer })
    }
  }

  return result
}

export const getWebPageData = ({
  data,
  locale,
  mainEntity,
  type = 'WebPage',
  primaryImageOfPage,
  hasPart,
  contentLocation,
}) => {
  const currentPath = window.location.pathname
  const url = data.url || `${baseUrl}${currentPath}`
  return {
    '@type': type,
    '@id': `${url}#webpage`,
    url,
    name: data.name || data.title,
    description: stripHtml(data.description) || undefined,
    inLanguage: data.contentLocale?.split('-')[0] || locale || undefined,
    isPartOf: currentPath.endsWith('/rootstech/') ? undefined : getWebSiteData(locale, false),
    hasPart: hasPart || undefined,
    mainEntity,
    primaryImageOfPage,
    contentLocation,
  }
}

// const getRegionPartPageData = (data) => {
//   return data.content?.map((region) => {
//     return getWebPageData({ data: region })
//   })
// }

export function WebPageStructuredData({ data, mainEntity }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language

  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="web-page-structured-data" />
}

export function SeriesPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getCreativeWorkSeriesData(data, locale)
  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="series-page-structured-data" />
}

export function RegistrationPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getRegistrationEventData(data)
  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="registration-page-structured-data" />
}

export function FAQPageStructuredData({ data, questionsAndAnswerNodes }) {
  if (!data || !questionsAndAnswerNodes || questionsAndAnswerNodes.length === 0) return null
  const locale = i18n.language
  const mainEntity = parseFaqQuestionAndAnswers(questionsAndAnswerNodes).map(({ question, answer }) =>
    getQuestionData({ question, answer })
  )
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'FAQPage' })

  return <StructuredData data={structuredData} dataTestId="faq-page-structured-data" />
}

export function HomePageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getRootsTechEventSeriesData()
  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="home-page-structured-data" />
}

export function AboutPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getRootsTechOrganizationData()
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'AboutPage' })

  return <StructuredData data={structuredData} dataTestId="about-page-structured-data" />
}

export function BoothPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getOrganizationData(data)
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'ItemPage' })

  return <StructuredData data={structuredData} dataTestId="booth-page-structured-data" />
}

export function SessionPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getCourseData(data)
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'ItemPage' })

  return <StructuredData data={structuredData} dataTestId="session-page-structured-data" />
}

export function SearchPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const structuredData = getWebPageData({ data, locale, type: 'SearchResultsPage' })

  return <StructuredData data={structuredData} dataTestId="search-page-structured-data" />
}

export function ExpoHallPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = data.content
    ?.filter((section) => section.items)
    ?.map((section) => getItemListData(section.items, section.title))
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'CollectionPage' })

  return <StructuredData data={structuredData} dataTestId="expo-hall-page-structured-data" />
}

export function SpeakersPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = data.content
    ?.filter((section) => section.items)
    ?.map((section) => getItemListData(section.items, section.title))
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'CollectionPage' })

  return <StructuredData data={structuredData} dataTestId="speakers-page-structured-data" />
}

export function SpeakerPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getPersonData(data)
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'ProfilePage' })

  return <StructuredData data={structuredData} dataTestId="speaker-page-structured-data" />
}

export function SponsorPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getCallForExhibitorsEventData(data)
  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="sponsor-page-structured-data" />
}

export function LibraryPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const structuredData = getWebPageData({ data, locale })

  return <StructuredData data={structuredData} dataTestId="library-page-structured-data" />
}

export function CallForContentPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getCallForContentEventData(data)
  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="call-for-content-page-structured-data" />
}

export function EventPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getEventData(data, locale)
  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="event-page-structured-data" />
}

export function EventsPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const subEvent = data.content
    ?.filter((section) => section.items)
    ?.map((section) => getEventData(section.items, section.title))
  const mainEntity = getEventSeriesData(data, locale, subEvent)
  const structuredData = getWebPageData({ data, locale, mainEntity })

  return <StructuredData data={structuredData} dataTestId="events-page-structured-data" />
}

export function PromotionsPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = getPromotionOfferData(data)
  const structuredData = getWebPageData({ data, locale, type: 'CollectionPage', mainEntity })

  return <StructuredData data={structuredData} dataTestId="promotions-page-structured-data" />
}

export function RegionCollectionPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  // TODO: research if hasPart is needed here
  // const hasPart = getRegionPartPageData(data)
  const mainEntity = data.content
    ?.filter((section) => section.items)
    ?.map((section) => getItemListData(section.items, section.title))
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'CollectionPage' })

  return <StructuredData data={structuredData} dataTestId="region-collection-page-structured-data" />
}

export function RegionPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = data.content
    ?.filter((section) => section.items)
    ?.map((section) => getItemListData(section.items, section.title))
  const contentLocation = getPlaceData({ name: data.name })
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'WebPage', contentLocation })

  return <StructuredData data={structuredData} dataTestId="region-page-structured-data" />
}

export function TopicCollectionPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language

  const mainEntity = data.content
    ?.filter((section) => section.items)
    ?.map((section) => getItemListData(section.items, section.title))
  const contentLocation = getPlaceData({ name: data.name })
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'CollectionPage', contentLocation })

  return <StructuredData data={structuredData} dataTestId="topics-page-structured-data" />
}

export function TopicPageStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const locale = i18n.language
  const mainEntity = data.content
    ?.filter((section) => section.items)
    ?.map((section) => getItemListData(section.items, section.title))
  const structuredData = getWebPageData({ data, locale, mainEntity, type: 'WebPage' })

  return <StructuredData data={structuredData} dataTestId="topic-page-structured-data" />
}
